/**
 * Document-level preferences: language + direction (QM-D08-001), theme dark/light/system and reduced
 * motion (QM-NC02-003). Pure helpers + one DOM applier so they are unit-testable.
 */
import { dirFor, isLocale, type Locale } from '@qm/i18n';
import type { UiPreferences } from '@qm/contracts/shell';

export type ResolvedTheme = 'light' | 'dark';

export function resolveTheme(pref: UiPreferences['theme'], systemDark: boolean): ResolvedTheme {
  return pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;
}
export function resolveMotion(pref: UiPreferences['motion'], systemReduce: boolean): 'reduce' | 'no-preference' {
  return pref === 'system' ? (systemReduce ? 'reduce' : 'no-preference') : pref;
}

export interface DocumentPrefs { locale: Locale; theme: ResolvedTheme; motion: 'reduce' | 'no-preference' }

/** The attributes the shell sets on <html>. data-brand is fixed: one theme key (QM-D02-003). */
export function documentAttributes(p: DocumentPrefs): Record<string, string> {
  return { lang: p.locale, dir: dirFor(p.locale), 'data-brand': 'quixo-marketing', 'data-theme': p.theme, 'data-motion': p.motion };
}

export function applyDocumentPrefs(el: { setAttribute(k: string, v: string): void }, p: DocumentPrefs): void {
  for (const [k, v] of Object.entries(documentAttributes(p))) el.setAttribute(k, v);
}

/** Pre-auth locale (sign-in pages) lives in localStorage; after sign-in the server preference wins. */
export const LOCALE_STORAGE_KEY = 'qm.locale';
export function storedLocale(storage: Pick<Storage, 'getItem'> | null | undefined): Locale | null {
  try {
    const v = storage?.getItem(LOCALE_STORAGE_KEY);
    return isLocale(v) ? v : null;
  } catch { return null; }
}
